import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, AfterViewInit, ViewChild, OnDestroy} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MaterialModule} from '../../../material.module';
import {DecoratedWebinar, StatusFilter, WebinarStatus} from '../webinars-admin.types';
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';
import {sortWebinarsByScheduleDate} from '../webinar-schedule-sort';
import {WebinarSeries} from '../../../models/webinar.model';
// no chip usage anymore

@Component({
    selector: 'app-webinars-list',
    standalone: true,
    imports: [CommonModule, MaterialModule, BreadcrumbsComponent],
    templateUrl: './webinars-list.component.html',
    styleUrls: ['./webinars-list.component.scss'],
    host: {class: 'webinars-m3 webinars-shell'},
})
export class WebinarsListComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() webinars: DecoratedWebinar[] = [];
    @Input() series: WebinarSeries[] = [];
    @Input() seriesLoadErrorMessage: string | null = null;
    @Input() statusCounts: Record<WebinarStatus, number> = {live: 0, upcoming: 0, draft: 0, past: 0};
    // Multi-select: array of WebinarStatus values. Empty means no restriction
    @Input() statusFilter: StatusFilter[] = [];
    @Input() filterTerm = '';
    @Input() selectedWebinarId: string | null = null;
    // Status metadata (no 'all')
    @Input() statusMeta: Record<WebinarStatus, { label: string; icon: string }> = {
        live: {label: 'Live now', icon: 'fiber_manual_record'},
        upcoming: {label: 'Upcoming', icon: 'schedule'},
        draft: {label: 'Draft', icon: 'pending'},
        past: {label: 'Completed', icon: 'history'},
    };

    @Output() refresh = new EventEmitter<void>();
    @Output() refreshLiveViewers = new EventEmitter<void>();
    @Output() create = new EventEmitter<void>();
    @Output() searchChange = new EventEmitter<string>();
    @Output() statusFilterChange = new EventEmitter<StatusFilter[]>();
    @Output() webinarSelected = new EventEmitter<DecoratedWebinar>();
    @Output() seriesSelected = new EventEmitter<WebinarSeries>();

    readonly displayedColumns: string[] = ['title', 'scheduleDate', 'time', 'provider', 'liveViewers', 'status'];
    readonly seriesDisplayedColumns: string[] = ['title', 'recurrence', 'nextOccurrence', 'timezone', 'occurrences'];
    readonly breadcrumbs = [{label: 'Shine Webinars', route: '/webinars'}, {label: 'Webinars'}];
    readonly statusFilters: Array<{ value: StatusFilter; label: string }> = [
        {value: 'live', label: 'Live now'},
        {value: 'upcoming', label: 'Upcoming'},
        {value: 'draft', label: 'Draft'},
        {value: 'past', label: 'Completed'},
    ];

    dataSource = new MatTableDataSource<DecoratedWebinar>([]);
    private liveRefreshTimer: any = null;
    private readonly liveRefreshIntervalMs = 10000;
    private skipPaginatorReset = false;

    @ViewChild(MatPaginator) paginator?: MatPaginator;
    @ViewChild(MatSort) sort?: MatSort;

    constructor() {
        this.dataSource.sortData = (data, sort) => {
            if (sort.active !== 'scheduleDate') {
                return data;
            }

            return sortWebinarsByScheduleDate(data, sort.direction);
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['webinars']) {
            const prev = changes['webinars'].previousValue as DecoratedWebinar[] | undefined;
            const curr = this.webinars ?? [];
            // If only data inside rows changed (e.g., liveViewers) and the ordering/ids match,
            // avoid resetting pagination.
            const stableUpdate =
                !!prev &&
                prev.length === curr.length &&
                curr.every((w, idx) => w.id === prev[idx]?.id);
            if (stableUpdate) {
                this.skipPaginatorReset = true;
            }

            this.dataSource.data = this.webinars ?? [];
            // Reset to first page when data changes to avoid out-of-range index
            if (this.paginator) {
                if (this.skipPaginatorReset) {
                    this.skipPaginatorReset = false;
                } else {
                    this.paginator.firstPage();
                }
            }
            this.updateLiveRefreshTimer();
        }
    }

    ngAfterViewInit(): void {
        if (this.sort) {
            this.dataSource.sort = this.sort;
        }

        if (this.paginator) {
            this.dataSource.paginator = this.paginator;
            // Show 10 items per page by default
            this.paginator.pageSize = 10;
        }
    }

    ngOnDestroy(): void {
        this.clearLiveRefreshTimer();
    }

    onSearch(event: Event): void {
        const target = event.target as HTMLInputElement | null;
        this.searchChange.emit(target?.value ?? '');
    }

    clearSearch(): void {
        this.searchChange.emit('');
    }

    // Multi-select dropdown change handler (no 'All' option)
    onStatusSelectChange(newValue: StatusFilter[] | null | undefined): void {
        const incoming = (newValue ?? []) as StatusFilter[];
        // Emit unique list (can be empty to mean no restriction)
        const unique = Array.from(new Set(incoming)) as StatusFilter[];
        this.statusFilterChange.emit(unique);
    }

    getSelectedStatusSummary(): string {
        const current = this.statusFilter ?? ([] as StatusFilter[]);
        if (!current.length) {
            // When "All" is effectively selected, show a neutral cue instead of duplicating "All"
            // in both the trigger and the first option in the dropdown.
            return 'Filter';
        }
        const labels = current
            .map((s) => this.statusFilters.find((f) => f.value === s)?.label || s)
            .join(', ');
        return labels;
    }

    selectRow(webinar: DecoratedWebinar): void {
        this.webinarSelected.emit(webinar);
    }

    selectSeries(series: WebinarSeries): void {
        this.seriesSelected.emit(series);
    }

    statusMetaFor(status: WebinarStatus): { label: string; icon: string } {
        return this.statusMeta[status];
    }

    // Keep formatting identical to Angular's date:'medium' output in most locales
    formatDate(input: string | Date): string {
        try {
            const d = input instanceof Date ? input : new Date(input);
            // Fallback if invalid date
            if (isNaN(d.getTime())) return 'TBA';
            return new Intl.DateTimeFormat(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
            }).format(d);
        } catch {
            return 'TBA';
        }
    }

    // Date-only formatting for Schedule Date column — fixed format dd.MM.yyyy
    formatDateOnly(input: string | Date | null | undefined): string {
        try {
            if (!input) return 'TBA';
            const d = input instanceof Date ? input : new Date(input);
            if (isNaN(d.getTime())) return 'TBA';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}.${mm}.${yyyy}`;
        } catch {
            return 'TBA';
        }
    }

    // Time range formatting for Time column (start–end). If end is missing, show only start.
    formatTimeRange(startInput: string | Date | null | undefined, endInput: string | Date | null | undefined): string {
        try {
            if (!startInput) return 'TBA';
            const start = startInput instanceof Date ? startInput : new Date(startInput);
            if (isNaN(start.getTime())) return 'TBA';
            const timeFmt = new Intl.DateTimeFormat(undefined, {timeStyle: 'short'});
            const startStr = timeFmt.format(start);
            if (!endInput) return startStr;
            const end = endInput instanceof Date ? endInput : new Date(endInput);
            if (isNaN(end.getTime())) return startStr;
            const endStr = timeFmt.format(end);
            return `${startStr} – ${endStr}`;
        } catch {
            return 'TBA';
        }
    }

    formatSeriesRule(series: WebinarSeries): string {
        const recurrence = series.recurrence;
        if (!recurrence) return 'No recurrence rule';
        const interval = recurrence.interval && recurrence.interval > 1 ? `Every ${recurrence.interval}` : 'Every';
        if (recurrence.frequency === 'weekly') {
            const days = (recurrence.daysOfWeek ?? []).join(', ');
            return `${interval} week${recurrence.interval > 1 ? 's' : ''}${days ? ` on ${days}` : ''}`;
        }
        if (recurrence.frequency === 'monthly') {
            return `${interval} month${recurrence.interval > 1 ? 's' : ''} from day ${recurrence.startsOn.slice(-2)}`;
        }
        return `${interval} day${recurrence.interval > 1 ? 's' : ''}`;
    }

    /**
     * Refresh click from the toolbar.
     * Clear filters (search + status) and then emit `refresh` so the parent reloads
     * data with defaults (no filters). Also update local inputs immediately so the
     * UI reflects the cleared state without waiting for parent round-trip.
     */
    onRefreshClick(): void {
        // 1) Clear local state so the UI (input + select) resets instantly
        this.filterTerm = '';
        this.statusFilter = [];

        // 2) Emit cleared filter values to the parent
        this.searchChange.emit('');
        this.statusFilterChange.emit([]);

        // 3) Then signal a data refresh
        this.refresh.emit();
        this.clearLiveRefreshTimer();
    }

    /** Indicates if any filters are currently active (used by empty-state copy/actions) */
    get hasActiveFilters(): boolean {
        const hasSearch = (this.filterTerm?.trim()?.length ?? 0) > 0;
        const hasStatuses = (this.statusFilter?.length ?? 0) > 0;
        return hasSearch || hasStatuses;
    }

    /** Clear filters without implying a full refresh beyond the existing behavior */
    onClearFiltersClick(): void {
        this.onRefreshClick();
    }

    /** Start/stop periodic refresh while any webinar is live to keep liveViewers updated */
    private updateLiveRefreshTimer(): void {
        const hasLive = (this.webinars ?? []).some((w) => w.status === 'live');
        if (hasLive) {
            this.startLiveRefreshTimer();
        } else {
            this.clearLiveRefreshTimer();
        }
    }

    private startLiveRefreshTimer(): void {
        if (this.liveRefreshTimer) return;
        this.liveRefreshTimer = setInterval(() => {
            // Prefer a lightweight refresh for live viewer counts. If the parent
            // handles it, we'll skip a full refresh. Otherwise, fall back to a
            // normal refresh but keep pagination position.
            this.refreshLiveViewers.emit();
            // Do not trigger the full refresh here to avoid repeated "updated" notifications.
            // Parent should handle refreshLiveViewers to update counts in place.
        }, this.liveRefreshIntervalMs);
    }

    private clearLiveRefreshTimer(): void {
        if (this.liveRefreshTimer) {
            clearInterval(this.liveRefreshTimer);
            this.liveRefreshTimer = null;
        }
    }
}
